import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import DemoPlayer from '@/components/feature/DemoPlayer';
import VideoCard from '@/components/feature/VideoCard';
import { getRelated, getVideoById, formatViews, filterVideos } from '@/lib/videos';
import { useFavorites } from '@/hooks/useFavorites';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const video = getVideoById(id);
  const { isFavorite, toggle } = useFavorites();
  const [feedback, setFeedback] = useState('');

  usePageMeta({
    title: video ? `${video.title} - 在线观看 -国产自拍精品视频 -无码中文字幕` : '内容不存在 - 无码中文字幕',
    description: video
      ? `${video.description.slice(0, 78)}…… ${video.title}（${video.year} / ${video.region} / ${video.duration}）详情与播放演示。`
      : '该内容不存在或已下架，可返回首页浏览其他日韩自拍视频。',
    keywords: video
      ? `${video.title},${video.category},${video.region}自拍视频,无码中文字幕`
      : '内容不存在,无码中文字幕',
  });

  if (!video) {
    return (
      <div className="mx-auto flex w-full max-w-[1280px] flex-col items-center px-4 py-24 text-center md:px-8">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-error-warning-line text-2xl" />
        </span>
        <h1 className="mt-5 font-heading text-2xl font-semibold text-foreground-950">内容不存在或已下架</h1>
        <p className="mt-3 max-w-[420px] text-[13px] leading-6 text-foreground-500">
          这条内容可能已被移除或链接有误。你可以返回首页，或通过分类导航继续浏览其他示范条目。
        </p>
        <div className="mt-6 flex flex-col gap-2 sm:flex-row">
          <Link
            to="/"
            className="flex h-10 items-center justify-center whitespace-nowrap rounded-md bg-primary-600 px-5 text-[13px] text-background-50 transition-colors hover:bg-primary-700"
          >
            返回首页
          </Link>
          <Link
            to="/search"
            className="flex h-10 items-center justify-center whitespace-nowrap rounded-md border border-background-300 px-5 text-[13px] text-foreground-700 transition-colors hover:bg-background-100"
          >
            搜索其他内容
          </Link>
        </div>
      </div>
    );
  }

  const related = getRelated(video, 6);
  const sameCategory = filterVideos({ category: video.category })
    .filter((item) => item.id !== video.id)
    .slice(0, 3);
  const favorited = isFavorite(video.id);

  const metaItems = [
    { icon: 'ri-calendar-line', label: '上传日期', value: video.uploadDate },
    { icon: 'ri-time-line', label: '时长', value: video.duration },
    { icon: 'ri-movie-2-line', label: '分类', value: video.category },
    { icon: 'ri-map-pin-line', label: '地区', value: video.region },
    { icon: 'ri-user-3-line', label: '视角', value: video.perspective },
    { icon: 'ri-eye-line', label: '观看', value: `${formatViews(video.views)}次` },
  ];

  const handleFavorite = () => {
    toggle(video.id);
    setFeedback(favorited ? '已取消本地收藏' : '已加入本地收藏');
    window.setTimeout(() => setFeedback(''), 2200);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setFeedback('详情页链接已复制');
    } catch {
      setFeedback('复制失败，请手动复制地址栏链接');
    }
    window.setTimeout(() => setFeedback(''), 2200);
  };

  return (
    <div className="w-full">
      <div className="mx-auto w-full max-w-[1280px] px-4 pt-6 md:px-8 md:pt-8">
        <nav className="flex flex-wrap items-center gap-1.5 text-[11px] text-foreground-500" aria-label="面包屑导航">
          <Link to="/" className="transition-colors hover:text-primary-700">
            首页
          </Link>
          <span className="flex h-3 w-3 items-center justify-center text-background-400">
            <i className="ri-arrow-right-s-line text-xs" />
          </span>
          <Link
            to={`/category/${encodeURIComponent(video.category)}`}
            className="transition-colors hover:text-primary-700"
          >
            {video.category}
          </Link>
          <span className="flex h-3 w-3 items-center justify-center text-background-400">
            <i className="ri-arrow-right-s-line text-xs" />
          </span>
          <span className="line-clamp-1 text-foreground-800">{video.title}</span>
        </nav>
      </div>

      <section className="mx-auto grid w-full max-w-[1280px] grid-cols-1 gap-8 px-4 py-6 md:px-8 md:py-8 lg:grid-cols-[1.55fr_1fr] lg:gap-10">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded bg-primary-50 px-2 py-0.5 text-[11px] text-primary-700">{video.category}</span>
            <span className="rounded bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-900">{video.region}</span>
            <span className="rounded border border-accent-300 px-2 py-0.5 text-[11px] text-accent-700">
              {video.resolution}
            </span>
          </div>

          <h1 className="mt-3 font-heading text-2xl font-semibold leading-9 text-foreground-950 md:text-[32px] md:leading-[1.35]">
            {video.title}
          </h1>

          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-[12px] text-foreground-500">
            {metaItems.map((item) => (
              <span key={item.label} className="flex items-center gap-1.5">
                <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
                  <i className={`${item.icon} text-sm`} />
                </span>
                <span className="text-foreground-400">{item.label}</span>
                <span className="text-foreground-800">{item.value}</span>
              </span>
            ))}
          </div>

          <div className="relative mt-5 aspect-video w-full overflow-hidden rounded-lg bg-primary-950">
            <img
              src={video.cover}
              alt={`${video.title} - ${video.category}自拍视频主封面`}
              title={`${video.title} - ${video.region}高清视频`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950/70 via-transparent to-transparent" />
            <div className="absolute inset-x-0 bottom-0 flex items-center justify-between px-4 pb-3">
              <span className="flex items-center gap-1.5 text-[11px] text-background-200">
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className="ri-image-line text-xs" />
                </span>
                主封面图 · 演示图源
              </span>
              <span className="rounded bg-primary-950/70 px-2 py-0.5 text-[11px] text-background-100">
                {video.duration}
              </span>
            </div>
          </div>

          <div className="mt-7">
            <h2 className="flex items-center gap-2 text-[15px] font-medium text-foreground-950">
              <span className="inline-block h-3.5 w-[3px] rounded-full bg-accent-500" />
              内容简介
            </h2>
            <p className="mt-3 text-[13px] leading-7 text-foreground-600">{video.description}</p>
            <p className="mt-2 text-[13px] leading-7 text-foreground-500">
              本条目为虚构示范内容，标题、封面与简介互相对应，用于展示分类浏览与详情页的信息结构，不对应任何真实作品。
            </p>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            {video.tags.map((tag) => (
              <Link
                key={tag}
                to={`/search?q=${encodeURIComponent(tag)}`}
                className="rounded-full border border-background-300 bg-background-50 px-2.5 py-1 text-[11px] text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                #{tag}
              </Link>
            ))}
          </div>

          <div className="mt-6 flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={handleFavorite}
              className={`flex h-11 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md px-6 text-[13px] transition-colors ${
                favorited
                  ? 'bg-accent-500 text-primary-950 hover:bg-accent-400'
                  : 'bg-primary-600 text-background-50 hover:bg-primary-700'
              }`}
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className={favorited ? 'ri-heart-fill text-base' : 'ri-heart-line text-base'} />
              </span>
              {favorited ? '已收藏到本地' : '收藏此内容'}
            </button>
            <button
              type="button"
              onClick={handleCopy}
              className="flex h-11 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 text-[13px] text-foreground-700 transition-colors hover:bg-background-100"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-link text-base" />
              </span>
              复制链接
            </button>
            {feedback && (
              <span className="flex h-11 items-center gap-1.5 text-[12px] text-accent-700">
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className="ri-check-line text-sm" />
                </span>
                {feedback}
              </span>
            )}
          </div>
        </div>

        <aside className="lg:sticky lg:top-24 lg:self-start">
          <DemoPlayer poster={video.cover} title={video.title} duration={video.duration} />

          <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-4">
            <div className="flex items-center justify-between">
              <h2 className="text-[13px] font-medium text-foreground-900">所属分类</h2>
              <span className="text-[11px] text-foreground-400">{sameCategory.length + 1} 部内容</span>
            </div>
            <Link
              to={`/category/${encodeURIComponent(video.category)}`}
              className="mt-3 flex items-center justify-between rounded-md bg-background-100 px-3 py-2.5 text-[13px] text-foreground-800 transition-colors hover:bg-background-200"
            >
              <span className="flex items-center gap-2">
                <span className="flex h-5 w-5 items-center justify-center text-primary-700">
                  <i className="ri-folder-3-line text-sm" />
                </span>
                进入「{video.category}」分类
              </span>
              <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
                <i className="ri-arrow-right-s-line" />
              </span>
            </Link>

            <div className="mt-3 flex flex-col gap-2">
              {sameCategory.map((item) => (
                <Link
                  key={item.id}
                  to={`/detail/${item.id}`}
                  className="group flex items-center gap-2.5 rounded-md p-1.5 transition-colors hover:bg-background-100"
                >
                  <span className="relative block h-11 w-[68px] shrink-0 overflow-hidden rounded bg-primary-950">
                    <img
                      src={item.cover}
                      alt={`${item.title} 封面缩略图`}
                      title={item.title}
                      className="h-full w-full object-cover object-top"
                    />
                  </span>
                  <span className="min-w-0">
                    <span className="line-clamp-1 block text-[12px] text-foreground-800 group-hover:text-primary-700">
                      {item.title}
                    </span>
                    <span className="mt-0.5 block text-[10px] text-foreground-400">
                      {item.duration} · {item.year}
                    </span>
                  </span>
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-4 flex items-start gap-2.5 rounded-lg border border-background-200 bg-background-100 p-3.5">
            <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-foreground-400">
              <i className="ri-information-line text-sm" />
            </span>
            <p className="text-[11px] leading-5 text-foreground-500">
              收藏状态仅保存在当前浏览器本地，清除浏览器数据后会失效。站内不提供账号登录与真实播放服务。
            </p>
          </div>
        </aside>
      </section>

      {related.length > 0 && (
        <section className="w-full border-t border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-8 md:py-12">
            <div className="flex items-end justify-between">
              <div>
                <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-accent-700">
                  <span className="inline-block h-[6px] w-[6px] rounded-full bg-accent-500" />
                  Related content
                </p>
                <h2 className="mt-2 font-heading text-xl font-semibold text-foreground-950">相关推荐</h2>
                <p className="mt-1.5 text-[12px] text-foreground-500">基于同分类、同地区与相同标签匹配的内容。</p>
              </div>
              <Link
                to={`/category/${encodeURIComponent(video.category)}`}
                className="hidden shrink-0 items-center gap-1 text-[12px] text-foreground-600 transition-colors hover:text-primary-700 sm:flex"
              >
                更多同类
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-arrow-right-line text-sm" />
                </span>
              </Link>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-x-3 gap-y-6 md:grid-cols-3 md:gap-x-5 lg:grid-cols-6">
              {related.map((item, index) => (
                <VideoCard key={item.id} video={item} index={index} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}